import React, { useState } from 'react';

import cn from 'classnames'
import Image from 'next/legacy/image';
import Link from 'next/link';
import SwiperCore, { Autoplay, Loop } from 'swiper/core';
import { Swiper, SwiperSlide } from 'swiper/react';

import { SocialAPI } from '@api/index';
import { ScrollLoading } from '@components/index';
import { NEWS_PATHS } from '@constants/index';
import { Refresh } from '@element/index';
import useClient from '@lib/hooks/useClient';
import { useIsomorphicLayoutEffect } from '@lib/hooks/useIsomorphicLayoutEffect';
import { isValidSource } from '@utils/sundries.js';
import { customizeRelativeTime } from '@utils/time';

import { Card } from '../common/HotNews';
import { useNewsPaging } from '../hooks';
import { parseHtml } from '../PostContent/util';

import 'swiper/css';
import styles from './index.module.less';

SwiperCore.use([Autoplay, Loop]);

const SwiperContainer = React.memo(function SwiperContainer ({ recommend }) {
  return (
    <Swiper
      autoplay={{
        delay: 3000,
        stopOnLastSlide: false,
        disableOnInteraction: true
      }}
      loop
    >
      {recommend?.result?.map((item) => (
        <SwiperSlide key={item.id}>
          <Card item={item} tgaName="h3" />
        </SwiperSlide>
      ))}
    </Swiper>
  );
});

const ImgContainer = React.memo(function ImgContainer ({ src }) {
  const isPC = useClient();
  let width = 180;
  let height = 120;
  if (!isPC) {
    width = 100;
    height = 76;
  }
  if (!isValidSource(src, 'image')) {
    // return (
    //   <div
    //     className={styles.illegalImg}
    //     style={{ width: `${width}px`, height: `${height}px` }}
    //   >
    //     {I18n.t('Kmmg8uqw')}
    //   </div>
    // );
    return null
  }
  return (
    <div className={styles.imgContainer}>
      <Image
        src={src}
        alt="Picture of the author"
        width={width}
        height={height}
        className={styles.leftImg}
      />
    </div>
  );
});

const InfoItem = React.memo(function InfoItem ({
  images = [],
  title,
  summary,
  timestamp,
  linkText,
  id
}) {
  const [richText, setRichText] = useState(parseHtml(summary)?.htmlText);
  useIsomorphicLayoutEffect(() => {
    setRichText(parseHtml(summary)?.htmlText);
  }, []);
  return (
    <Link legacyBehavior href={`${NEWS_PATHS.POST}/${id}`}>
      <a target="_black" className={styles.infoItem} data-buried="ZX-09">
        {images[0] ? <ImgContainer src={images[0]} /> : null}

        <div className={styles.InfoItemContent}>
          <div className={styles.infoHeader}>
            <div className={styles.infoSource}>{linkText}</div>
            <div className={styles.time}>
              {customizeRelativeTime(timestamp)}
            </div>
          </div>
          <h3 className={cn(styles.title, 'fontMedium')}>{title}</h3>
          <div
            className={styles.content}
            dangerouslySetInnerHTML={{
              __html: richText || summary
            }}
          />
        </div>
      </a>
    </Link>
  );
});

export const FocusNews = React.memo(function FocusNews ({
  squareStory: { result = [], nextId = '' } = {},
  squareStory = { result: [] },
  recommend
}) {
  const { list, fetchData, status, isRefresh } = useNewsPaging({
    defaultList: result,
    nextId,
    defaultStatus: 'success',
    fetchFn: SocialAPI.getSquareStory,
    fetchParams: {
      limit: 20,
      id_lte: nextId,
      verb: 13
    },
    needToken: true
  });

  return (
    <div className={styles.container}>
      <Refresh
        callback={fetchData}
        refreshing={status !== 'success' && isRefresh}
      />
      <div className={styles.swiperContainer}>
        <SwiperContainer recommend={recommend} />
      </div>
      <ScrollLoading
        invisibleHeight={100}
        fetchData={fetchData}
        status={status}
      >
        {list.map((item, i) => (
          <InfoItem key={item.id} {...item} />
        ))}
      </ScrollLoading>
    </div>
  );
});
